import type { ExecutionProgressHydration } from '../executionOperations/types';
import type { RouteExecutionProjection } from '../routes/execution/routeExecutionProjectionTypes';
import type { ExecutionIncident } from '../routes/execution/incidentFacade';
import { buildEffectiveExecutionView } from '../routes/execution/routeExecutionProjectionFacade';
import type { OperationalAttention } from '../operationalDomain/processes/operationalProcessTypes';
import {
  hoursAgo,
  latestIso,
  healthStateLabel,
  INTEL_CARGO_INACTIVE_HOURS,
  INTEL_PARTIAL_STALL_HOURS,
  INTEL_STOP_STUCK_HOURS,
  INTEL_OVERDUE_PLANNED_HOURS
} from './intelligenceHeuristics';
import type {
  ExecutionAnomaly,
  ExecutionAnomalyProjection,
  ExecutionHealthProjection,
  ExecutionHealthState,
  ExecutionProgressionAnalysis,
  EtaFoundationProjection,
  OperationalRecommendation,
  RiskSeverity,
  RouteOperationalIntelligence,
  RouteRiskItem,
  RouteRiskProjection
} from './types';

export type BuildIntelligenceParams = {
  hydration: ExecutionProgressHydration;
  incidents?: ExecutionIncident[];
  now?: Date;
};

function severityRank(s: RiskSeverity): number {
  return { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 }[s];
}

function maxSeverity(risks: RouteRiskItem[]): RiskSeverity | null {
  if (!risks.length) return null;
  return risks.reduce((best, r) =>
    severityRank(r.severity) > severityRank(best) ? r.severity : best
  , risks[0].severity);
}

function collectLastActivity(
  stops: ExecutionProgressHydration['stops']
): { lastAt: string | null; hours: number | null } {
  const now = new Date();
  const lastAt = latestIso(
    stops.flatMap((s) => [
      s.actualAt,
      s.arrivedAt,
      s.loadingStartedAt,
      s.loadingCompletedAt,
      s.unloadingStartedAt,
      s.unloadingCompletedAt
    ])
  );
  return { lastAt, hours: hoursAgo(lastAt, now) };
}

export function buildRouteRiskProjection(params: {
  hydration: ExecutionProgressHydration;
  projection: RouteExecutionProjection;
  incidents: ExecutionIncident[];
  progression: ExecutionProgressionAnalysis;
  now: Date;
}): RouteRiskProjection {
  const risks: RouteRiskItem[] = [];
  const { hydration, projection, incidents, progression, now } = params;
  const seen = new Set<string>();

  const add = (item: RouteRiskItem) => {
    if (seen.has(item.code)) return;
    seen.add(item.code);
    risks.push(item);
  };

  for (const stop of hydration.stops) {
    if (stop.isCompleted || !stop.plannedAt) continue;
    const overdueH = hoursAgo(stop.plannedAt, now);
    if (overdueH != null && overdueH > INTEL_OVERDUE_PLANNED_HOURS) {
      const unloading = String(stop.type).toLowerCase() === 'unloading';
      add({
        code: unloading ? 'overdue_unload' : 'stalled_stop',
        severity: overdueH > 12 ? 'HIGH' : 'MEDIUM',
        title: unloading ? 'Просрочена выгрузка' : 'Просрочена точка',
        detail: `План ${new Date(stop.plannedAt).toLocaleString('ru-RU')}`
      });
    }
    if (!stop.isCompleted && stop.completionPercent > 0 && stop.completionPercent < 100) {
      const last = latestIso([
        stop.unloadingStartedAt,
        stop.loadingStartedAt,
        stop.arrivedAt
      ]);
      const h = hoursAgo(last, now);
      if (h != null && h >= INTEL_STOP_STUCK_HOURS) {
        add({
          code: 'stalled_stop',
          severity: 'MEDIUM',
          title: 'Точка зависла',
          detail: `Нет завершения ${Math.round(h)} ч`
        });
      }
    }
  }

  if (projection.partialExecution.hasPartialDelivery) {
    const h = progression.hoursSinceLastActivity;
    if (h != null && h >= INTEL_PARTIAL_STALL_HOURS) {
      add({
        code: 'partial_unload_stagnation',
        severity: 'HIGH',
        title: 'Частичная выгрузка не завершена',
        detail: `Без прогресса ${Math.round(h)} ч`
      });
    }
  }

  const openDowntime = incidents.filter(
    (i) => i.status === 'open' && i.incidentType === 'downtime'
  );
  if (openDowntime.length > 0) {
    const hours = openDowntime[0].downtimeHours ?? 4;
    add({
      code: 'long_downtime',
      severity: hours >= 8 ? 'HIGH' : 'MEDIUM',
      title: 'Простой не закрыт',
      detail: `${hours} ч`
    });
  }

  const openBlocking = incidents.filter((i) => i.status === 'open' && i.blockingFlag);
  if (openBlocking.length > 0) {
    add({
      code: 'unresolved_incident',
      severity: 'HIGH',
      title: 'Открытый блокирующий инцидент',
      detail: `${openBlocking.length} шт.`
    });
  }

  if (projection.progress.blockers.includes('cargo_undelivered')) {
    add({
      code: 'blocked_cargo',
      severity: 'CRITICAL',
      title: 'Груз не доставлен',
      detail: 'Завершение заблокировано'
    });
  }

  if (!projection.progress.executionReady && projection.progress.blockers.length > 0) {
    add({
      code: 'completion_blocked',
      severity: 'MEDIUM',
      title: 'Завершение недоступно',
      detail: projection.progress.blockers.join(', ')
    });
  }

  if (projection.executionConsistency.blockerCount > 2) {
    add({
      code: 'execution_inconsistency',
      severity: 'MEDIUM',
      title: 'Несогласованность исполнения',
      detail: `${projection.executionConsistency.blockerCount} блокеров`
    });
  }

  if (progression.isStalled) {
    add({
      code: 'sla_pressure',
      severity: 'HIGH',
      title: 'Риск срыва SLA',
      detail: progression.stalledReason ?? 'Нет прогресса'
    });
  }

  risks.sort((a, b) => severityRank(b.severity) - severityRank(a.severity));
  return { risks, highestSeverity: maxSeverity(risks) };
}

export function buildExecutionProgressionAnalysis(params: {
  hydration: ExecutionProgressHydration;
  projection: RouteExecutionProjection;
  now: Date;
}): ExecutionProgressionAnalysis {
  const { hydration, projection } = params;
  const { hours } = collectLastActivity(hydration.stops);

  const cargoMoving =
    projection.cargoProgress.some((c) => c.loadedQty > 0) &&
    (projection.vehicleLoad.totalLoaded > projection.vehicleLoad.totalUnloaded ||
      projection.progress.cargoPercent > 0);

  const isStalled =
    !projection.progress.executionComplete &&
    hours != null &&
    hours >= INTEL_CARGO_INACTIVE_HOURS &&
    projection.vehicleLoad.totalRemaining > 0;

  let stalledReason: string | null = null;
  if (isStalled) {
    stalledReason = `Нет изменений груза ${Math.round(hours!)} ч`;
  } else if (projection.partialExecution.hasPartialDelivery && hours != null && hours >= INTEL_PARTIAL_STALL_HOURS) {
    stalledReason = `Частичная выгрузка без прогресса ${Math.round(hours)} ч`;
  }

  const hasProgression =
    projection.progress.overallPercent > 0 ||
    hydration.stops.some((s) => s.isCompleted) ||
    cargoMoving;

  return {
    hasProgression,
    isStalled: Boolean(stalledReason) || isStalled,
    stalledReason,
    hoursSinceLastActivity: hours,
    cargoFlowMoving: cargoMoving
  };
}

export function buildExecutionHealthProjection(params: {
  risks: RouteRiskProjection;
  progression: ExecutionProgressionAnalysis;
  projection: RouteExecutionProjection;
  incidents: ExecutionIncident[];
}): ExecutionHealthProjection {
  const reasons: string[] = [];
  const { risks, progression, projection, incidents } = params;

  for (const r of risks.risks.slice(0, 3)) {
    reasons.push(r.detail ? `${r.title}: ${r.detail}` : r.title);
  }
  if (progression.stalledReason) reasons.push(progression.stalledReason);

  let state: ExecutionHealthState = 'HEALTHY';

  const hasCriticalRisk = risks.risks.some((r) => r.severity === 'CRITICAL');
  const hasHighRisk = risks.risks.some((r) => r.severity === 'HIGH');
  const openBlocking = incidents.some((i) => i.status === 'open' && i.blockingFlag);

  if (hasCriticalRisk || openBlocking) {
    state = 'CRITICAL';
  } else if (projection.progress.blockers.length > 0 && !projection.progress.executionReady) {
    state = 'BLOCKED';
  } else if (progression.isStalled) {
    state = 'STALLED';
  } else if (hasHighRisk || risks.risks.some((r) => r.severity === 'MEDIUM')) {
    state = 'DELAYED';
  }

  if (projection.progress.executionComplete) {
    state = 'HEALTHY';
    reasons.length = 0;
    reasons.push('Исполнение завершено');
  }

  const summary = reasons[0] ?? 'Прогресс в пределах нормы';

  return {
    state,
    label: healthStateLabel(state),
    summary,
    reasons: reasons.slice(0, 4)
  };
}

export function buildExecutionAnomalyProjection(params: {
  progression: ExecutionProgressionAnalysis;
  projection: RouteExecutionProjection;
  hydration: ExecutionProgressHydration;
  incidents: ExecutionIncident[];
}): ExecutionAnomalyProjection {
  const anomalies: ExecutionAnomaly[] = [];
  const { progression, projection, hydration } = params;

  if (progression.hoursSinceLastActivity != null && progression.hoursSinceLastActivity >= INTEL_CARGO_INACTIVE_HOURS) {
    anomalies.push({
      code: 'cargo_inactive',
      title: 'Груз не меняется слишком долго',
      detail: `${Math.round(progression.hoursSinceLastActivity)} ч без активности`
    });
  }

  if (progression.isStalled) {
    anomalies.push({
      code: 'no_progression',
      title: 'Нет прогресса исполнения',
      detail: progression.stalledReason ?? 'Застой'
    });
  }

  if (projection.partialExecution.hasPartialDelivery) {
    const h = progression.hoursSinceLastActivity;
    if (h != null && h >= INTEL_PARTIAL_STALL_HOURS) {
      anomalies.push({
        code: 'partial_unload_frozen',
        title: 'Частичная выгрузка заморожена',
        detail: `${Math.round(h)} ч`
      });
    }
  }

  const stuckStop = hydration.stops.find(
    (s) => !s.isCompleted && s.completionPercent > 0 && s.completionPercent < 100
  );
  if (stuckStop) {
    anomalies.push({
      code: 'stop_stalled',
      title: 'Точка в промежуточном состоянии',
      detail: stuckStop.city ?? stuckStop.address ?? stuckStop.executionStopUid
    });
  }

  return { anomalies: anomalies.slice(0, 5) };
}

export function buildEtaFoundationProjection(params: {
  hydration: ExecutionProgressHydration;
  projection: RouteExecutionProjection;
  progression: ExecutionProgressionAnalysis;
  risks: RouteRiskProjection;
}): EtaFoundationProjection {
  const { hydration, projection, progression, risks } = params;
  const incomplete = hydration.stops.filter((s) => !s.isCompleted && s.plannedAt);
  const lastPlanned = incomplete.length
    ? incomplete[incomplete.length - 1].plannedAt
    : null;

  let expectedCompletionLabel: string | null = null;
  if (lastPlanned) {
    expectedCompletionLabel = new Date(lastPlanned).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  let delayRiskLabel: string | null = null;
  if (progression.isStalled) {
    delayRiskLabel = 'Вероятная задержка';
  } else if (risks.risks.some((r) => r.code === 'overdue_unload' || r.code === 'sla_pressure')) {
    delayRiskLabel = 'Риск задержки';
  }

  const slaBreachRisk = risks.risks.some(
    (r) => r.code === 'sla_pressure' || r.severity === 'CRITICAL'
  );

  const paceLabel =
    projection.progress.overallPercent >= 80
      ? 'Темп нормальный'
      : projection.progress.overallPercent >= 40
        ? 'Темп средний'
        : projection.progress.overallPercent > 0
          ? 'Темп низкий'
          : 'Темп не начат';

  return {
    expectedCompletionLabel,
    delayRiskLabel,
    slaBreachRisk,
    paceLabel
  };
}

export function buildOperationalRecommendations(params: {
  health: ExecutionHealthProjection;
  risks: RouteRiskProjection;
  anomalies: ExecutionAnomalyProjection;
  progression: ExecutionProgressionAnalysis;
}): OperationalRecommendation[] {
  const items: OperationalRecommendation[] = [];
  const push = (id: string, priority: number, title: string, reason: string) => {
    items.push({ id, priority, title, reason });
  };

  if (params.progression.isStalled) {
    push('contact_driver', 1, 'Связаться с водителем', params.progression.stalledReason ?? 'Застой');
  }
  if (params.risks.risks.some((r) => r.code === 'partial_unload_stagnation')) {
    push('check_partial', 2, 'Проверить частичную выгрузку', 'Долгое зависание частичной выгрузки');
  }
  if (params.risks.risks.some((r) => r.code === 'long_downtime')) {
    push('confirm_downtime', 2, 'Подтвердить простой', 'Открытый простой');
  }
  if (params.risks.risks.some((r) => r.code === 'stalled_stop')) {
    push('complete_stalled_stop', 3, 'Завершить зависшую точку', 'Точка в промежуточном статусе');
  }
  if (params.risks.risks.some((r) => r.code === 'overdue_unload')) {
    push('check_unload', 3, 'Проверить выгрузку', 'Просрочен план выгрузки');
  }
  if (params.risks.risks.some((r) => r.code === 'unresolved_incident')) {
    push('resolve_incident', 1, 'Закрыть блокирующий инцидент', 'Инцидент блокирует исполнение');
  }

  return items.sort((a, b) => a.priority - b.priority).slice(0, 5);
}

export function buildHealthAwareAttentions(
  routeId: number,
  health: ExecutionHealthProjection,
  risks: RouteRiskProjection
): OperationalAttention[] {
  const items: OperationalAttention[] = [];
  let idx = 0;

  if (health.state === 'STALLED') {
    items.push({
      id: `intel:stalled:${routeId}:${idx++}`,
      severity: 'warning',
      source: 'execution',
      relatedProcessId: null,
      relatedProcessType: null,
      blocking: false,
      requiresAction: true,
      title: 'Исполнение остановилось',
      detail: health.summary,
      expiresAt: null,
      ownerUserId: null,
      assignedDepartment: 'OPERATIONS',
      escalationLevel: 0,
      acknowledged: false,
      acknowledgedAt: null
    });
  }

  if (health.state === 'CRITICAL' || health.state === 'BLOCKED') {
    items.push({
      id: `intel:critical:${routeId}:${idx++}`,
      severity: 'critical',
      source: 'execution',
      relatedProcessId: null,
      relatedProcessType: null,
      blocking: true,
      requiresAction: true,
      title: health.state === 'CRITICAL' ? 'Критическая задержка' : 'Исполнение заблокировано',
      detail: health.reasons[0] ?? health.summary,
      expiresAt: null,
      ownerUserId: null,
      assignedDepartment: 'OPERATIONS',
      escalationLevel: 1,
      acknowledged: false,
      acknowledgedAt: null
    });
  }

  for (const r of risks.risks.filter((x) => x.severity === 'HIGH' || x.severity === 'CRITICAL').slice(0, 2)) {
    items.push({
      id: `intel:risk:${routeId}:${r.code}:${idx++}`,
      severity: r.severity === 'CRITICAL' ? 'critical' : 'warning',
      source: 'execution',
      relatedProcessId: null,
      relatedProcessType: null,
      blocking: r.code === 'blocked_cargo',
      requiresAction: true,
      title: r.title,
      detail: r.detail,
      expiresAt: null,
      ownerUserId: null,
      assignedDepartment: 'OPERATIONS',
      escalationLevel: r.severity === 'CRITICAL' ? 1 : 0,
      acknowledged: false,
      acknowledgedAt: null
    });
  }

  return items;
}

export function buildRouteOperationalIntelligence(
  params: BuildIntelligenceParams
): RouteOperationalIntelligence | null {
  const effective = buildEffectiveExecutionView(params.hydration, { cockpit: true });
  const projection = effective?.projection;
  if (!projection) return null;

  const now = params.now ?? new Date();
  const incidents = params.incidents ?? [];

  const progression = buildExecutionProgressionAnalysis({
    hydration: params.hydration,
    projection,
    now
  });
  const risks = buildRouteRiskProjection({
    hydration: params.hydration,
    projection,
    incidents,
    progression,
    now
  });
  const health = buildExecutionHealthProjection({ risks, progression, projection, incidents });
  const anomalies = buildExecutionAnomalyProjection({
    progression,
    projection,
    hydration: params.hydration,
    incidents
  });
  const eta = buildEtaFoundationProjection({ hydration: params.hydration, projection, progression, risks });
  const recommendations = buildOperationalRecommendations({ health, risks, anomalies, progression });
  const healthAttentions = buildHealthAwareAttentions(params.hydration.routeId, health, risks);

  return {
    routeId: params.hydration.routeId,
    health,
    risks,
    progression,
    anomalies,
    eta,
    recommendations,
    healthAttentions
  };
}
